import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Clickable `<th>` content for a server-sorted column — used internally by {@link Table} for
 * `Column.sortKey`, and directly by hand-built participant tables (Registered, Accepted,
 * Unattended, Attended) that render their own `<table>` markup instead of using `Table`. Keeps
 * the sort-arrow/`aria-sort`/screen-reader-status behavior identical everywhere a column header
 * is sortable.
 */
export function SortableColumnHeader({
  label,
  field,
  activeField,
  order,
  onSort,
}: {
  label: string
  field: string
  activeField: string | undefined
  order: 'asc' | 'desc' | undefined
  onSort: (field: string) => void
}) {
  const { t } = useTranslation()
  const isSorted = field === activeField
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="focus-visible:ring-pax-blue/40 hover:text-pax-black inline-flex items-center gap-1 rounded focus-visible:ring-2 focus-visible:outline-none"
    >
      <span>{label}</span>
      <span aria-hidden="true">{isSorted ? (order === 'desc' ? '▼' : '▲') : ''}</span>
      <span className="sr-only">
        {isSorted
          ? order === 'desc'
            ? t('table_sort_desc')
            : t('table_sort_asc')
          : t('table_sort_none')}
      </span>
    </button>
  )
}

/** Definition for a single column in the Table component. */
export interface Column<T> {
  header: string
  accessor?: keyof T
  render?: (row: T) => ReactNode
  headerClassName?: string
  cellClassName?: string
  /**
   * Server-side sort field this column controls (must match a backend-whitelisted field, e.g.
   * `listParticipants`'s `sort` param). Omit for non-sortable columns. When set, the header
   * renders as a clickable button that reports clicks via the table's `onSort` prop instead of
   * the plain `<th>` text.
   */
  sortKey?: string
  /**
   * Visual case for this column's header text. Defaults to `'uppercase'` (existing behaviour,
   * e.g. OrgPanel's tables). Event tables use `'none'` (#274) — i.e. render the i18n string's
   * own authored sentence case ("Name", "Presentation") rather than forcing any transform —
   * pass it explicitly rather than fighting the default via `headerClassName`, since two
   * text-transform utilities on the same element don't reliably override by class order.
   */
  headerTextTransform?: 'uppercase' | 'lowercase' | 'none'
  /**
   * Accessible name for the header cell when it has no visible text (e.g. an Actions column
   * left visually blank per #274). Applied as the `<th>`'s `aria-label`, which takes priority
   * over its (empty) text content, so the column keeps a real name for screen readers/tests.
   */
  headerAriaLabel?: string
}

interface Props<T extends object> {
  columns: Column<T>[]
  rows: T[]
  keyExtractor: (row: T) => string
  emptyMessage?: string
  /** Enable leading checkbox column */
  selectable?: boolean
  selectedKeys?: Set<string>
  onSelectRow?: (key: string, checked: boolean) => void
  onSelectAll?: (checked: boolean) => void
  /** Custom aria-label for each row checkbox; defaults to table_select_row with the row key */
  rowAriaLabel?: (row: T) => string
  /** Override the outer container className (e.g. pass "" when the parent already provides a border/card shell) */
  containerClassName?: string
  /**
   * Optional mobile card renderer. When provided:
   * - The table is hidden on phones (hidden md:block)
   * - A stacked card list (md:hidden) is rendered using this function per row
   * When omitted, the table is always visible (existing behaviour).
   */
  renderCard?: (row: T, isSelected: boolean, onSelect: (checked: boolean) => void) => ReactNode
  /**
   * Override the mobile card-list wrapper className. Useful when a parent already wraps the
   * table in its own white card shell (e.g. {@link OrgPanel}) — on white-on-white backgrounds
   * the individual cards' own border/shadow alone don't read as clearly separated rows, so the
   * parent can pass a tinted background + padding here to make each card visually pop.
   */
  cardsContainerClassName?: string
  /** Field currently sorted by (matches a column's `sortKey`), for `aria-sort` and arrow direction. */
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  /** Called with a column's `sortKey` when its header button is clicked. */
  onSort?: (field: string) => void
}

/** PROAXIV-branded data table with optional selectable rows and mobile card fallback. */
export function Table<T extends object>({
  columns,
  rows,
  keyExtractor,
  emptyMessage,
  selectable = false,
  selectedKeys,
  onSelectRow,
  onSelectAll,
  rowAriaLabel,
  containerClassName = 'border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm',
  renderCard,
  cardsContainerClassName = 'flex flex-col gap-3 md:hidden',
  sortField,
  sortOrder,
  onSort,
}: Props<T>) {
  const { t } = useTranslation()
  const resolvedEmpty = emptyMessage ?? t('table_empty')
  const allSelected = selectable && rows.length > 0 && selectedKeys?.size === rows.length
  const someSelected = selectable && (selectedKeys?.size ?? 0) > 0 && !allSelected

  const tableBlock = (
    <div className={containerClassName}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
              {selectable && (
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label={t('select_all')}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                  />
                </th>
              )}
              {columns.map((col, i) => {
                const isSorted = col.sortKey != null && col.sortKey === sortField
                const ariaSort =
                  col.sortKey == null
                    ? undefined
                    : isSorted
                      ? sortOrder === 'desc'
                        ? 'descending'
                        : 'ascending'
                      : 'none'
                return (
                  <th
                    key={i}
                    aria-sort={ariaSort}
                    aria-label={col.headerAriaLabel}
                    className={[
                      'text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider',
                      (col.headerTextTransform ?? 'uppercase') === 'none'
                        ? ''
                        : (col.headerTextTransform ?? 'uppercase'),
                      col.headerClassName ?? '',
                    ].join(' ')}
                  >
                    {col.sortKey != null && onSort ? (
                      <SortableColumnHeader
                        label={col.header}
                        field={col.sortKey}
                        activeField={sortField}
                        order={sortOrder}
                        onSort={onSort}
                      />
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="text-pax-gray px-4 py-10 text-center text-sm"
                >
                  {resolvedEmpty}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = keyExtractor(row)
                const isSelected = selectedKeys?.has(key) ?? false

                return (
                  <tr
                    key={key}
                    className={[
                      'border-pax-purple/10 border-b transition-colors last:border-0',
                      isSelected ? 'bg-pax-light-blue/40' : 'hover:bg-pax-light-purple/40',
                    ].join(' ')}
                  >
                    {selectable && (
                      <td className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={
                            rowAriaLabel ? rowAriaLabel(row) : t('table_select_row', { name: key })
                          }
                          checked={isSelected}
                          onChange={(e) => onSelectRow?.(key, e.target.checked)}
                          className="accent-pax-blue h-4 w-4 cursor-pointer rounded"
                        />
                      </td>
                    )}
                    {columns.map((col, ci) => (
                      <td
                        key={ci}
                        className={['text-pax-black px-4 py-3', col.cellClassName ?? ''].join(' ')}
                      >
                        {col.render
                          ? col.render(row)
                          : col.accessor != null
                            ? String(row[col.accessor] ?? '')
                            : null}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )

  // No card renderer — always show the table (original behaviour)
  if (!renderCard) return tableBlock

  // Card renderer provided: table on desktop, cards on mobile
  return (
    <>
      {/* Desktop table */}
      <div data-testid="desktop-view" className="hidden md:block">
        {tableBlock}
      </div>

      {/* Mobile card list */}
      <div data-testid="mobile-view" className={cardsContainerClassName}>
        {rows.length === 0 ? (
          <p className="text-pax-gray px-1 py-6 text-center text-sm">{resolvedEmpty}</p>
        ) : (
          rows.map((row) => {
            const key = keyExtractor(row)
            const isSelected = selectedKeys?.has(key) ?? false
            return (
              <div key={key}>
                {renderCard(row, isSelected, (checked) => onSelectRow?.(key, checked))}
              </div>
            )
          })
        )}
      </div>
    </>
  )
}
